import { type ResultOf } from '@graphql-typed-document-node/core';
import { useToast } from '@medely/ui-kit';
import { gateNames } from '@mp/constants';
import { graphql, parseFragmentData } from '@mp/graphql/generated';
import { useCurrentUser } from '@mp/hooks';
import { useGate } from './useGate';
import { useGraphQLQuery } from './useGraphQLRequest';
import { useMissingConditionsCg } from './useMissingConditionsCg';

const RecommendedConditionFieldsFragment = graphql(`
  fragment RecommendedConditionFields on Condition {
    id
    slug
    label
    payload
    condition_type
    display_category
    display_description
    condition_documents {
      id
      name
      category
      file
    }
    qualifications {
      display_category
      id
      input_source
      instructions
      long_name
      slug
      admin_view_only
      qualification_type
      status
      short_name
      conditions {
        id
      }
      qualification_attributes {
        id
        name
        field_type
        required
        data
        input_source
        active
        slug
      }
      default_verification_title
      qualification_automations {
        id
        description
        name
        loading_text
        is_active
        qualification_automation_attributes {
          id
          qualification_attribute {
            id
            field_type
            slug
            name
            data
            input_source
            active
          }
          required
        }
      }
    }
  }
`);

const RecommendedCredentialsJobCountsGql = graphql(`
  query RecommendedCredentialsJobCounts {
    recommendedCredentialsJobCounts {
      open_jobs_count
      recently_posted_jobs_count
      conditions {
        recently_posted_jobs_count
        condition {
          ...RecommendedConditionFields
        }
      }
    }
  }
`);

type RecommendedCredentialsJobCountsGqlResult = ResultOf<typeof RecommendedCredentialsJobCountsGql>;
export type RecommendedCondition = ResultOf<typeof RecommendedConditionFieldsFragment>;

export type RecommendedCredential = {
  condition: RecommendedCondition;
  recently_posted_jobs_count: number;
};

type UseRecommendedCredentialsJobCountsResult = {
  isEnabled: boolean;
  conditions: RecommendedCredential[];
  conditionCount: number;
  openJobsCount: number;
  recentlyPostedJobsCount: number;
  isLoading: boolean;
  refetch: () => Promise<unknown>;
};

export const useRecommendedCredentialsJobCounts = (): UseRecommendedCredentialsJobCountsResult => {
  const isEnabled = useGate(gateNames.recommendedCredentialsDemand);
  const { currentUser } = useCurrentUser();
  const toast = useToast();
  const onError = () => toast.error('Sorry, something went wrong');

  const enabled = isEnabled && !!currentUser?.professional;

  const { data, isError, isLoading, isInitialLoading, refetch } =
    useGraphQLQuery<RecommendedCredentialsJobCountsGqlResult>({
      enabled,
      onError,
      operationName: 'RecommendedCredentialsJobCounts',
      query: RecommendedCredentialsJobCountsGql,
      variables: {},
    });

  const currentRecommended = useMissingConditionsCg({
    recommended: true,
    enabled: !isEnabled,
    onError,
  });

  if (!isEnabled) {
    const conditions = currentRecommended.isError
      ? []
      : currentRecommended.conditions.map((condition) => ({
          condition,
          recently_posted_jobs_count: 0,
        }));

    return {
      isEnabled,
      conditions,
      conditionCount: conditions.length,
      openJobsCount: 0,
      recentlyPostedJobsCount: 0,
      isLoading: currentRecommended.isLoading,
      refetch: currentRecommended.refetchMissingConditions,
    };
  }

  const jobCounts = isError ? undefined : data?.recommendedCredentialsJobCounts;
  const conditions = (jobCounts?.conditions ?? []).map(
    ({ condition, recently_posted_jobs_count }) => ({
      condition: parseFragmentData(RecommendedConditionFieldsFragment, condition),
      recently_posted_jobs_count,
    }),
  );

  return {
    isEnabled,
    conditions,
    conditionCount: conditions.length,
    openJobsCount: jobCounts?.open_jobs_count ?? 0,
    recentlyPostedJobsCount: jobCounts?.recently_posted_jobs_count ?? 0,
    isLoading: enabled ? isLoading : isInitialLoading,
    refetch,
  };
};
